import React, {forwardRef, PropsWithChildren} from 'react';
import styled, {css} from 'styled-components';

import {
  buildInteractionIdProps,
  InteractionComponentProps,
} from '../../../../../../libs/shared/helpers/src/interaction/interactionHelpers';
import {TimelineVariationsEnum} from '../../../../../store/timelineVariations/timelineVariationSelectors';

/*
 * Props.
 */

export interface MessageViewerBaseProps extends InteractionComponentProps {
  className?: string;
  isChatFollowUp?: boolean;
  isSameTypeAsPrevious?: boolean;
  isPopup?: boolean;
  isFirst?: boolean;
  timelineVariation: TimelineVariationsEnum;
  isInComposerOnBottom?: boolean;
}

/*
 * Style.
 */

interface MessageStyleProps {
  $isChatFollowUp?: boolean;
  $isSameTypeAsPrevious?: boolean;
  $isPopup?: boolean;
  $isFirst?: boolean;
  $timelineVariation: TimelineVariationsEnum;
  $isInComposerOnBottom?: boolean;
}
const StyledMessageDiv = styled.div<MessageStyleProps>`
  display: flex;
  flex-direction: column;

  margin-top: ${(p) => getMarginTopWithBuffer(p)}px;

  ${(p) => addMessageStyles(p.$timelineVariation, Boolean(p.$isInComposerOnBottom))}
`;
const addMessageStyles = ($timelineVariation: TimelineVariationsEnum, $isInComposerOnBottom: boolean) =>
  $timelineVariation === TimelineVariationsEnum.SYNCHRONOUS && !$isInComposerOnBottom
    ? css`
        width: fit-content;
        padding-left: 39px;
        min-height: 36px;

        // Needed to ensure Seen indicator doesn't overlap More icon.
        margin-right: 206px;
      `
    : css`
        max-width: 100%;
        min-height: 42px;
      `;

function getMarginTopWithBuffer(props: MessageStyleProps) {
  if (props.$timelineVariation === TimelineVariationsEnum.ASYNCHRONOUS) {
    return getMarginTop(props);
  }

  const bufferSize = props.$isFirst ? 18 : 28;
  const marginTopBuffer = !props.$isChatFollowUp ? bufferSize : 0;

  return getMarginTop(props) + marginTopBuffer;
}

function getMarginTop(props: MessageStyleProps) {
  if (props.$isPopup) {
    return 0;
  }

  if (props.$isChatFollowUp) {
    return 4;
  }

  if (props.$isSameTypeAsPrevious) {
    return 10;
  }

  if (props.$isFirst) {
    return 3;
  }

  return 16;
}

/*
 * Component.
 */

export const MessageViewerBase = forwardRef<HTMLDivElement, PropsWithChildren<MessageViewerBaseProps>>(
  (props, ref) => (
    <StyledMessageDiv
      className={props.className}
      $isChatFollowUp={props.isChatFollowUp}
      $isSameTypeAsPrevious={props.isSameTypeAsPrevious}
      $isPopup={props.isPopup}
      $isFirst={props.isFirst}
      $timelineVariation={props.timelineVariation}
      $isInComposerOnBottom={props.isInComposerOnBottom}
      ref={ref}
      {...buildInteractionIdProps(props.interactionId)}
    >
      {props.children}
    </StyledMessageDiv>
  ),
);
